/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTableOfContents } from '~/hooks/useTableOfContents'
import { useMediaQuery } from 'usehooks-ts'

type TableOfContentsProps = {
  containerId?: string
  showNested?: boolean
  altHeading?: string
}

const TableOfContents = ({
  containerId = 'content',
  showNested = true,
  altHeading = 'Table of contents'
}: TableOfContentsProps) => {
  const { toc, activeId } = useTableOfContents(containerId)
  const effectiveActiveId = activeId || (toc.length > 0 ? toc[0].id : undefined)

  const isMobile = useMediaQuery('(max-width: 768px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  const handleClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    history.replaceState(null, '', `#${id}`)
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <nav>
      <h2 className='mb-m text-h4-sm md:text-h4-md lg:text-h4-lg font-bold'>
        {altHeading}
      </h2>
      {toc.length === 0 ? (
        <div className='space-y-s border-l-blue-d-20 list-none border-l-2'>
          <p className='text-blue-d-60 pl-m'>{`${altHeading} is loading...`}</p>
        </div>
      ) : (
        <ul className='space-y-s pl-s border-l-blue-d-20 pr-2xs max-h-[calc(100vh-250px)] list-none overflow-auto border-l-2'>
          {toc.map(section => {
            const isSectionActive =
              effectiveActiveId === section.id ||
              section.children.some((c: any) => c.id === activeId)

            return (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className={`unstyled-link transition-colors duration-300 ${isSectionActive ? 'text-blue-d font-bold' : 'text-blue-d-60 hover:underline'}`}
                  onClick={handleClick(section.id)}
                >
                  {section.text}
                </a>

                {showNested &&
                  !isMobile &&
                  section.children.length > 0 &&
                  // Below will show all h3s if the h2 is active
                  (effectiveActiveId === section.id ||
                    section.children.some((c: any) => c.id === activeId)) && (
                    <ul className='space-y-s mt-xs pl-s list-[style-type:"-"]'>
                      {section.children.map(child => (
                        <li key={child.id} className='pl-2xs'>
                          <a
                            href={`#${child.id}`}
                            className={`unstyled-link relative transition-all duration-300 ease-in-out ${activeId === child.id ? 'text-blue-d font-bold' : 'text-blue-d-60 hover:underline'}`}
                            onClick={handleClick(child.id)}
                          >
                            {child.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
              </li>
            )
          })}
        </ul>
      )}
    </nav>
  )
}

export default TableOfContents
